import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _class;
import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { Resource } from '@jobmatchme/suspendable-resource/es/Resource';
import { useResource } from '@jobmatchme/suspendable-resource/es/hooks';
import { Session, UnauthenticatedSession } from '@jobmatchme/auth/es/Session';
import API, { AuthRoutes } from '@jobmatchme/auth/es/API';
import Enviroment from '@jobmatchme/auth/es/Enviroment';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/function';
import * as T from 'fp-ts/lib/Task';
import { APIFailure } from '@jobmatchme/auth/es/APIErrors';
import { jsx as _jsx } from "react/jsx-runtime";
export const initialState = {
  isLoading: true,
  isError: false,
  user: null,
  isAuthenticated: false,
  isInternal: false,
  error: undefined
};
export class ProviderAction {
  constructor(effect) {
    /**
     * Run the underlying effect and throw any occuring errors
     * @throws {APIFailure} provider error forwarding error message from remote
     * @returns {Promise<T>} the promise containing the result of the effect on successfull execution
     */
    _defineProperty(this, "throwOnFailure", () => pipe(this.effect, T.map(result => pipe(result, E.fold(err => {
      throw new APIFailure(err);
    }, success => success))))());
    this.effect = effect;
  }
}

/**
 * Create a Auth Session user resource.
 * This creates a resource based on the given promise, that suspends if the data has not finished loading
 *
 * @returns {Resource<Session>} suspending resource
 */
_class = ProviderAction;
_defineProperty(ProviderAction, "make", effect => new _class(effect));
export const createSessionResource = (api = new API(new AuthRoutes(Enviroment))) => {
  return new Resource(pipe(api.getUserData(), T.flatMap(res => pipe(res, E.map(user => T.of(Session.makeAuthenticated(user, document.cookie))), E.getOrElse(() => {
    const partialSession = Session.maybePartial(document.cookie);
    if (partialSession) {
      return T.of(partialSession);
    }
    return T.of(Session.makeUnauthenticated());
  })))));
};
export const AuthContext = /*#__PURE__*/React.createContext(Session.makeUnauthenticated());
export const AuthActionsContext = /*#__PURE__*/React.createContext({
  login: () => {
    throw new Error('Function not implemented.');
  },
  register: () => {
    throw new Error('Function not implemented.');
  },
  refreshCache: () => {
    throw new Error('Function not implemented.');
  },
  logout: function () {
    throw new Error('Function not implemented.');
  },
  deleteAccount: function () {
    throw new Error('Function not implemented.');
  },
  addContact: function () {
    throw new Error('Function not implemented.');
  },
  deleteContact: function () {
    throw new Error('Function not implemented.');
  },
  updateUserData: function () {
    throw new Error('Function not implemented.');
  },
  requestContactVerificationToken: function () {
    throw new Error('Function not implemented.');
  },
  verifyContact: function () {
    throw new Error('Function not implemented.');
  },
  setMainContact: function () {
    throw new Error('Function not implemented.');
  },
  requestPasswordReset: function () {
    throw new Error('Function not implemented.');
  },
  resetPassword: function () {
    throw new Error('Function not implemented.');
  },
  startMigration: function () {
    throw new Error('Function not implemented.');
  },
  finishMigration: function () {
    throw new Error('Function not implemented.');
  },
  changePassword: function () {
    throw new Error('Function not implemented.');
  }
});

/**
 * Auth Provider with suspence support.
 * Improves Performance of UI by suspending components if the auth session has not loaded the user yet.
 */
const AuthProvider = ({
  children,
  sessionResource,
  cookieStore,
  api = new API(new AuthRoutes(Enviroment))
}) => {
  let _cookieStore = '';
  if (cookieStore !== void 0) {
    _cookieStore = cookieStore;
  } else if (typeof document !== 'undefined') {
    _cookieStore = document.cookie;
  }
  const [state, setState] = useResource(sessionResource);
  const refreshSession = useCallback(() => pipe(api.refresh(), T.flatMap(result => pipe(result, E.fold(() => T.of(setState(UnauthenticatedSession)), () => T.of(setState(s => {
    const prevSession = s;
    const newSession = Session.makeAuthenticated(prevSession.user, _cookieStore);
    return newSession;
  })))))), [api, _cookieStore, setState]);

  /**
   * Refresh session on reload / provider mount
   */
  useEffect(() => {
    if (Session.isAuthenticated(_cookieStore)) {
      if (state.isAuthenticated) {
        refreshSession()();
      }
    }
  }, []);

  /**
   * Watch session cookie expiry
   */
  useEffect(() => {
    if (!Session.isAuthenticated(_cookieStore) && state.isAuthenticated) {
      setState(UnauthenticatedSession);
    }
  });

  /**
   * Register timer to refresh session before expiry
   */
  const refreshTimerRef = useRef(undefined);
  useEffect(() => {
    if (Session.isAuthenticated(_cookieStore)() && state.isAuthenticated) {
      return pipe(state.expiry, O.map(expiry => {
        const currentUnix = Math.floor(Date.now() / 1000);
        let timeout = expiry - currentUnix - 3600;
        if (timeout < 1) {
          timeout = 0;
        }
        refreshTimerRef.current = setTimeout(() => {
          refreshSession()();
          refreshTimerRef.current = void 0;
        }, timeout);
        return refreshTimerRef.current;
      }), O.fold(() => undefined, timeoutRef => () => {
        clearTimeout(timeoutRef);
        refreshTimerRef.current = void 0;
      }));
    }
  }, [_cookieStore, cookieStore, refreshSession, state.expiry, state.isAuthenticated]);

  /**
   * Login or Register and create a session.
   * Possible login flows are either a user / password based login flow or an totp flow
   * @param {LoginRequest} params the credentials for authentication. Needs to contain a 'user' (either phone or email) and optionally a password
   * @returns {ProviderAction<LoginResponse>} if the login is successfull a new session is created. In case of an totp flow, a partial session is created and a second login request containing the otp needs to be performed
   */
  const auth = useCallback(params => ProviderAction.make(pipe(api.auth(params), T.flatMap(res => pipe(res, E.map(session => {
    return pipe(T.of(setState(session)), T.map(() => E.right(session)));
  }), E.getOrElse(err => T.of(E.left(err))))))), [api, setState]);

  /**
   * Login and create a session.
   * Possible login flows are either a user / password based login flow or an totp flow
   * @param {LoginRequest} params the credentials for authentication. Needs to contain a 'user' (either phone or email) and optionally a password
   * @returns {ProviderAction<LoginResponse>} if the login is successfull a new session is created. In case of an totp flow, a partial session is created and a second login request containing the otp needs to be performed
   */
  const login = useCallback(params => auth(params), [auth]);

  /**
   * Register a new user
   * @param {RegistrationRequest} params the credentials for registration
   * @returns {ProviderAction<RegistrationResponse>} a state indicting success or failure
   */
  const register = useCallback(params => auth(params), [auth]);

  /**
   * Fetch the latest userdata for the currently authenticated user and refresh the current session
   * @returns {ProviderAction<Session>} returns the latest session from the remote, bypassing the local cache
   */
  const refreshCache = useCallback(() => ProviderAction.make(pipe(api.getUserData(), T.map(res => pipe(res, E.map(user => Session.makeAuthenticated(user, _cookieStore)), E.getOrElse(() => Session.makeUnauthenticated()))), T.flatMap(session => pipe(T.of(setState(session)), T.map(() => E.right(session)))))), [_cookieStore, api, setState]);

  /**
   * Add a new contact to user
   * @param {ContactType} contactType the type of contact to add
   * @param {string}      contact     The contact to add
   * @returns {ProviderAction<string>} a state indicting success or failure
   */
  const addContact = useCallback((contactType, contact) => ProviderAction.make(pipe(api.addContact(contactType, contact), T.map(res => pipe(res, E.map(() => 'success'))))), [api]);

  /**
   * Request a verification token for a contact
   * @param {ContactType} contactType the type of contact to add
   * @param {string}      contact     The contact to add
   * @returns {ProviderAction<string>} a state indicting success or failure
   */
  const requestContactVerificationToken = useCallback((contactType, contact) => ProviderAction.make(pipe(api.requestContactVerificationToken(contactType, contact), T.map(res => pipe(res, E.map(() => 'success'))))), [api]);

  /**
   * Verify a contact with a recieved token
   * @param {ContactType} contactType the type of contact to add
   * @param {string}      contact     The contact to add
   * @returns {ProviderAction<string>} a state indicting success or failure
   */
  const verifyContact = useCallback((contactType, token) => ProviderAction.make(pipe(api.verifyContact(contactType, token), T.map(res => pipe(res, E.map(() => 'success'))))), [api]);

  /**
   * Make a contact the users main contact
   * @param {ContactType} contactType the type of contact to add
   * @param {string}      contact     The contact to add
   * @returns {ProviderAction<string>} a state indicting success or failure
   */
  const setMainContact = useCallback((contactType, contact) => ProviderAction.make(pipe(api.setMainContact(contactType, contact), T.map(res => pipe(res, E.map(() => 'success'))))), [api]);

  /**
   * Delete a contact from user
   * @param {ContactType} contactType the type of contact to add
   * @param {string}      contact     The contact to add
   * @returns {ProviderAction<string>} a state indicting success or failure
   */
  const deleteContact = useCallback((contactType, contact) => ProviderAction.make(pipe(api.deleteContact(contactType, contact), T.map(res => pipe(res, E.map(() => 'success'))))), [api]);

  /**
   * Edit the users personal data
   * @param {ContactType} contactType the type of contact to add
   * @param {string}      contact     The contact to add
   * @returns {ProviderAction<string>} a state indicting success or failure
   */
  const updateUserData = useCallback(newUserParams => ProviderAction.make(pipe(api.updateUserData(newUserParams), T.map(res => pipe(res, E.map(() => 'success'))))), [api]);

  /**
   * Logout and terminate the current session
   * @returns {ProviderAction<void>}
   */
  const logout = useCallback(() => ProviderAction.make(T.apFirst(pipe(T.of(setState(Session.makeUnauthenticated()))))(api.logout())), [api, setState]);

  /**
   * Delete the current account
   * @returns {ProviderAction<void>}
   */
  const deleteAccount = useCallback(() => ProviderAction.make(pipe(api.deleteAccount())), [api, setState]);

  /**
   * Request a password reset
   * @returns {ProviderAction<void>}
   */
  const requestPasswordReset = useCallback(email => ProviderAction.make(pipe(api.requestPasswordReset(email), T.map(res => pipe(res, E.map(() => 'success'))))), [api, setState]);

  /**
   * Reset the password
   * @returns {ProviderAction<void>}
   */
  const resetPassword = useCallback((token, newPassword) => ProviderAction.make(pipe(api.resetPassword(token, newPassword))), [api, setState]);

  /**
   * Change the password
   * @returns {ProviderAction<void>}
   */
  const changePassword = useCallback((oldPassword, newPassword) => ProviderAction.make(pipe(api.changePassword(oldPassword, newPassword))), [api, setState]);
  const actions = useMemo(() => ({
    login,
    register,
    addContact,
    deleteContact,
    refreshCache,
    logout,
    updateUserData,
    requestContactVerificationToken,
    verifyContact,
    setMainContact,
    requestPasswordReset,
    resetPassword,
    changePassword,
    deleteAccount
  }), [addContact, deleteContact, login, logout, refreshCache, register, updateUserData, requestContactVerificationToken, verifyContact, setMainContact, requestPasswordReset, resetPassword, changePassword, deleteAccount]);
  return /*#__PURE__*/_jsx(AuthContext.Provider, {
    value: state,
    children: /*#__PURE__*/_jsx(AuthActionsContext.Provider, {
      value: actions,
      children: children
    })
  });
};
export default /*#__PURE__*/React.memo(AuthProvider);